/**
 * Renders one Briefing section's stored prose text.
 *
 * Sections 1, 2, 3, and 5 exist in Briefing.content_json ONLY as a
 * single free-text string per section (see ui/README.md's Repository
 * Audit — there is no structured per-item breakdown for these sections
 * today). This component renders that text as plain, safely-escaped
 * React text nodes — no HTML parsing, no dangerouslySetInnerHTML — with
 * a light structural pass so "- " / "• " prefixed lines render as an
 * actual list rather than a wall of unbroken text, since that is the
 * only formatting convention Claude's own output reliably uses.
 */
function isBulletLine(line: string): boolean {
  return /^[-•]\s+/.test(line.trim());
}

const NO_CONTENT_TEXT = "Nuk ka përmbajtje në këtë seksion.";

export function PlainTextBlock({ text }: { text: string }) {
  const lines = text.split(/\r?\n/);

  const blocks: { type: "list" | "para"; lines: string[] }[] = [];
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;
    const bullet = isBulletLine(line);
    const last = blocks[blocks.length - 1];
    if (last && last.type === (bullet ? "list" : "para")) {
      last.lines.push(line);
    } else {
      blocks.push({ type: bullet ? "list" : "para", lines: [line] });
    }
  }

  if (blocks.length === 0) {
    return <p className="text-sm italic text-ink-400">{NO_CONTENT_TEXT}</p>;
  }

  return (
    <div className="space-y-3 text-sm leading-relaxed text-ink-700">
      {blocks.map((block, i) =>
        block.type === "list" ? (
          <ul key={i} className="list-disc space-y-1.5 pl-5">
            {block.lines.map((line, j) => (
              <li key={j}>{line.replace(/^[-•]\s+/, "")}</li>
            ))}
          </ul>
        ) : (
          <p key={i}>{block.lines.join(" ")}</p>
        ),
      )}
    </div>
  );
}
